'use client';

import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';

const SILENT_AUDIO_URL = '/api/audio/keep-alive';

interface BackgroundAudioContextType {
  startLock: () => void;
  stopLock: () => void;
  updateMetadata: (data: {
    title?: string;
    artist?: string;
    album?: string;
    artworkSrc?: string;
  }) => void;
  // We expose a ref so the controller can toggle audio without re-rendering the app
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

const BackgroundAudioContext = createContext<BackgroundAudioContextType | null>(null);

export function useBackgroundAudio() {
  const context = useContext(BackgroundAudioContext);
  if (!context) {
    throw new Error('useBackgroundAudio must be used within a BackgroundAudioProvider');
  }
  return context;
}

export function BackgroundAudioProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Initialize the audio element once on mount
    const audio = new Audio(location.origin + SILENT_AUDIO_URL);
    audio.loop = true;

    /**
     * FIX: VOLUME HIJACKING & BLUETOOTH ROUTING
     * 0.001 is inaudible but still registers as active media.
     * This prevents Bluetooth devices from latching onto the silent track
     * while keeping the background/MediaSession lock rock-solid.
     */
    audio.volume = 0.001;

    // @ts-expect-error Property 'playsInline' does not exist on type 'HTMLAudioElement'
    audio.playsInline = true; // Crucial for iOS
    audioRef.current = audio;

    return () => {
      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };
  }, []);

  const startLock = useCallback(() => {
    if (!audioRef.current) return;

    // Must be called synchronously from a user gesture
    audioRef.current.play().catch((err) => {
      console.warn('Failed to start background audio lock:', err);
    });

    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'playing';
    }
  }, []);

  const stopLock = useCallback(() => {
    // 1. Hard stop the audio
    if (audioRef.current && audioRef.current.src) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0; // Reset to start
      audioRef.current.src = ''; // Force the browser to drop the media buffer
    }

    // 2. Tear down the Media Session completely
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'none';
      navigator.mediaSession.metadata = null; // This clears the lock screen UI
    }
  }, []);

  const updateMetadata = useCallback(
    ({
      title,
      artist,
      album,
      artworkSrc,
    }: {
      title?: string;
      artist?: string;
      album?: string;
      artworkSrc?: string;
    }) => {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: title || 'Livestream',
          artist: artist || '',
          album: album || 'Gotedo Streams',
          artwork: artworkSrc ? [{ src: artworkSrc, sizes: '512x512', type: 'image/png' }] : [],
        });
      }
    },
    [],
  );

  return (
    <BackgroundAudioContext.Provider value={{ startLock, stopLock, updateMetadata, audioRef }}>
      {children}
    </BackgroundAudioContext.Provider>
  );
}
